import { useMemo } from 'react';
import { useAppDataSourceRecords, useDataSourceRecords } from '../../stores/DataSourceRecordStore';
import {
  useBlocksFromPage,
  useInterfaceAppStore,
  useInterfaceStore,
} from '../../stores/InterfaceStore';
import type { ComputeContextMode } from './const';
import {
  useGetAppInterfaceEntityMethods,
  useGetPageInterfaceEntityMethods,
} from './useGetInterfaceEntityMethods';

export function usePageInterfaceEntityMethods({
  mode,
  getComputeContext,
}: {
  mode: ComputeContextMode;
  getComputeContext?: () => Record<string, unknown>;
}) {
  const blocks = useBlocksFromPage();
  const dataSources = useDataSourceRecords();
  const pageVariables = useInterfaceStore().hooks.page.usePageVariables();
  const pageFunctions = useInterfaceStore().hooks.page.usePageFunctions();

  const {
    getActionsForComputeContext,
    getBlockMethodsForComputeContext,
    getDataSourceMethodsForComputeContext,
    getLocalStorageMethodsForComputeContext,
    getPageVariableMethodsForComputeContext,
    getSessionStorageMethodsForComputeContext,
    getPageFunctionMethodsForComputeContext,
  } = useGetPageInterfaceEntityMethods({ mode, getComputeContext });

  // biome-ignore lint/correctness/useExhaustiveDependencies: added blocks to maintain reactivity such that when blocks are added or removed, the block methods are recalculated
  const blockMethods = useMemo(() => {
    return getBlockMethodsForComputeContext();
    // oxlint-disable-next-line exhaustive-deps -- explained above
  }, [blocks, getBlockMethodsForComputeContext]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: added data sources to maintain reactivity such that when data sources are added or removed, the dataSource methods are recalculated
  const dataSourceMethods = useMemo(() => {
    return getDataSourceMethodsForComputeContext();
    // oxlint-disable-next-line exhaustive-deps -- explained above
  }, [dataSources, getDataSourceMethodsForComputeContext]);

  const localStorageMethods = useMemo(() => {
    return getLocalStorageMethodsForComputeContext();
  }, [getLocalStorageMethodsForComputeContext]);

  const actions = useMemo(() => {
    return getActionsForComputeContext();
  }, [getActionsForComputeContext]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: added page variables to maintain reactivity such that when page variables are added or removed, the page variable methods are recalculated
  const pageVariableMethods = useMemo(() => {
    return getPageVariableMethodsForComputeContext();
    // oxlint-disable-next-line exhaustive-deps -- explained above
  }, [pageVariables, getPageVariableMethodsForComputeContext]);

  const sessionStorageMethods = useMemo(() => {
    return getSessionStorageMethodsForComputeContext();
  }, [getSessionStorageMethodsForComputeContext]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: added page functions to maintain reactivity such that when page functions are added or removed, the page function methods are recalculated
  const pageFunctionMethods = useMemo(() => {
    return getPageFunctionMethodsForComputeContext();
    // oxlint-disable-next-line exhaustive-deps -- explained above
  }, [pageFunctions, getPageFunctionMethodsForComputeContext]);

  return {
    blockMethods,
    dataSourceMethods,
    pageVariableMethods,
    pageFunctionMethods,
    localStorageMethods,
    sessionStorageMethods,
    actions,
  };
}

export function useAppInterfaceEntityMethods({
  mode,
  getComputeContext,
}: {
  mode: ComputeContextMode;
  getComputeContext?: () => Record<string, unknown>;
}) {
  const dataSources = useAppDataSourceRecords();
  const {
    getActionsForAppComputeContext,
    getDataSourceMethodsForAppComputeContext,
    getLocalStorageMethodsForAppComputeContext,
    getPageVariableMethodsForAppComputeContext,
    getSessionStorageMethodsForAppComputeContext,
    getPageFunctionMethodsForAppComputeContext,
  } = useGetAppInterfaceEntityMethods({ mode, getComputeContext });

  const appPageVariables = useInterfaceAppStore().hooks.page.usePageVariables();
  const appPageFunctions = useInterfaceAppStore().hooks.page.usePageFunctions();

  // biome-ignore lint/correctness/useExhaustiveDependencies: added data sources to maintain reactivity such that when data sources are added or removed, the dataSource methods are recalculated
  const dataSourceMethods = useMemo(() => {
    return getDataSourceMethodsForAppComputeContext();
    // oxlint-disable-next-line exhaustive-deps -- explained above
  }, [dataSources, getDataSourceMethodsForAppComputeContext]);

  const localStorageMethods = useMemo(() => {
    return getLocalStorageMethodsForAppComputeContext();
  }, [getLocalStorageMethodsForAppComputeContext]);

  const sessionStorageMethods = useMemo(() => {
    return getSessionStorageMethodsForAppComputeContext();
  }, [getSessionStorageMethodsForAppComputeContext]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: added appPageVariables to maintain reactivity such that when app page variables are added or removed, the page variable methods are recalculated
  const pageVariableMethods = useMemo(() => {
    return getPageVariableMethodsForAppComputeContext();
    // oxlint-disable-next-line exhaustive-deps -- explained above
  }, [appPageVariables, getPageVariableMethodsForAppComputeContext]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: added appPageFunctions to maintain reactivity such that when app page functions are added or removed, the page function methods are recalculated
  const pageFunctionMethods = useMemo(() => {
    return getPageFunctionMethodsForAppComputeContext();
    // oxlint-disable-next-line exhaustive-deps -- explained above
  }, [appPageFunctions, getPageFunctionMethodsForAppComputeContext]);

  const actions = useMemo(() => {
    return getActionsForAppComputeContext();
  }, [getActionsForAppComputeContext]);

  return {
    dataSourceMethods,
    localStorageMethods,
    sessionStorageMethods,
    pageVariableMethods,
    pageFunctionMethods,
    actions,
  };
}
